import {Alert} from 'src/design/atoms/Alert';
import {Box} from 'src/design/atoms/Box';
import {Divider} from 'src/design/atoms/Divider';
import {IconAlertTriangle} from '@tabler/icons-react';
import {useEffect, useMemo, useState} from 'react';
import {Link, useParams} from 'react-router-dom';
import type {CourseSessionSimpleResponse} from 'src/api/ProactApiClient';
import {
  AttachmentModuleEnum,
  CourseAssignmentStateEnum,
  EntityIdentifierEnum,
  FeatureFlagEnum,
  ModuleEnum,
  NotificationModuleEnum,
  PermissionOperationEnum,
} from 'src/api/ProactApiClient';
import {
  useDeleteCourseAssignmentMutation,
  useGetCourseAssignmentQuery,
  useTransitionCourseAssignmentStateMutation,
} from 'src/redux/queries/courseAssignment';
import {useGetCourseSessionListQuery} from 'src/redux/queries/courseSession';
import {Button} from 'src/design/atoms/Button';
import {Spinner} from 'src/design/atoms/Spinner';
import {BackBar} from 'src/app/layout/BackBar';
import {ContentWrapper} from 'src/app/layout/ContentWrapper';
import {PageHeader} from 'src/design/organisms/PageHeader';
import {ConfirmDialog} from 'src/design/molecules/ConfirmDialog';
import {PermissionGuard} from 'src/app/security/authorization/PermissionGuard';
import {SecondaryActionPanel} from 'src/design/molecules/SecondaryActionPanel';
import {FormViewComponentV2} from 'src/app/forms/FormViewComponent/FormViewComponentV2';
import {AttachmentsModule} from 'src/app/attachments/AttachmentsModule';
import {AuditModule} from 'src/app/audit-logs/AuditModule';
import {NotificationsModule} from 'src/app/notifications/NotificationsModule';
import {DataHeader} from 'src/design/organisms/DataHeader';
import {DataList} from 'src/design/organisms/DataList';
import {useListState} from 'src/design/organisms/useListState';
import {routeRegistry} from 'src/pages/RouteRegistry';
import {useAuditLogPermissions} from 'src/shared/auth/permissions/useAuditLogPermissions';
import {useModulePermissions} from 'src/shared/auth/permissions/useModulePermissions';
import {usePersonnelPermissions} from 'src/shared/auth/permissions/usePersonnelPermissions';
import {PathUtility} from 'src/shared/constants/PathBuilder';
import type {CourseAssignmentViewType} from 'src/shared/constants/ViewTypes';
import {useAlert} from 'src/shared/hooks/useAlert';
import useSetDocumentTitle from 'src/shared/hooks/useSetDocumentTitle';
import {useUrlQueryTabV2} from 'src/shared/hooks/useUrlQueryTabV2';
import {useBackNavigation} from 'src/shared/hooks/useBackNavigation';
import {useTrainingBreadcrumbs} from 'src/shared/hooks/useTrainingBreadcrumbs';
import {parseErrorMessage} from 'src/shared/utils/parseErrorMessage';
import type {IFormDataV2} from 'src/types/FormV2';
import {useDebounce} from 'usehooks-ts';
import {useDisclosure} from '@mantine/hooks';
import {CourseAssignmentPillConfig} from 'src/app/training/courseAssignmentPillConfig';
import {CourseAssignmentFormMeta} from '../../../../app/training/courseFormMeta';
import {
  CourseSessionPillConfig,
  TrainingIcon,
} from '../../../../app/training/coursePills';
import {CreateDefaultCourseAssignment} from '../../../../app/training/courseDefaults';
import {TrainingDocumentTitle} from '../../../../app/training/courseContentValidation';
import {ViewUserCourseClassrooms} from '../shared/viewCourseDefinition/ViewUserCourseClassrooms';
import {useAppNavigate} from 'src/shared/hooks/useAppNavigate';

const maxPageRows = 10;

export const ViewCourseDefinitionAssignmentPage = () => {
  useSetDocumentTitle(TrainingDocumentTitle);
  const {definitionId, assignmentId, view} = useParams();

  const trainingPerms = useModulePermissions(ModuleEnum.Training);
  const personnelPerms = usePersonnelPermissions();
  const auditPerms = useAuditLogPermissions();

  const [formMeta] = useState(CourseAssignmentFormMeta);

  const {setAlert} = useAlert();
  const navigate = useAppNavigate();
  const {
    searchTerm,
    setSearchTerm,
    currentPage,
    handlePageChange,
    start,
    length,
  } = useListState({
    identifier: `course-assignment-sessions-${assignmentId ?? 'new'}`,
    pageSize: maxPageRows,
  });
  const [isTransitionSaving, setIsTransitionSaving] = useState(
    CourseAssignmentStateEnum.Invalid
  );
  const [confirmDeleteOpened, confirmDeleteHandlers] = useDisclosure(false);
  const debouncedSearchTerm = useDebounce(searchTerm, 500);

  const {
    data: assignment,
    isLoading: isAssignmentLoading,
    error: assignmentError,
  } = useGetCourseAssignmentQuery(parseInt(assignmentId!), {
    skip: !assignmentId,
  });

  const data = assignment ?? CreateDefaultCourseAssignment(definitionId);
  const formData = useMemo(() => data as IFormDataV2, [data]);

  const {
    data: sessionListData,
    isLoading: isSessionListLoading,
    error: sessionListError,
  } = useGetCourseSessionListQuery(
    {
      start,
      length,
      search: debouncedSearchTerm,
      sortColumn: 'StartedAt',
      sortDirection: 'desc',
      assignmentId: parseInt(assignmentId!),
      sessionUserId: data.assignedToId,
    },
    {
      skip: !definitionId || !assignmentId || data.assignedToId === undefined,
    }
  );

  const isLoading = isAssignmentLoading || isSessionListLoading;
  const indexListData = sessionListData?.data;
  const totalRecords = sessionListData?.totalRecords ?? 0;

  const [transitionCourseAssignmentState] =
    useTransitionCourseAssignmentStateMutation();
  const [deleteCourseAssignment] = useDeleteCourseAssignmentMutation();

  useEffect(() => {
    if (assignmentError) {
      setAlert(parseErrorMessage(assignmentError), 'error');
    }
  }, [assignmentError]);

  useEffect(() => {
    if (sessionListError) {
      setAlert(parseErrorMessage(sessionListError), 'error');
    }
  }, [sessionListError]);

  const handeTransitionAssignment = async (
    state: CourseAssignmentStateEnum
  ) => {
    try {
      if (assignmentId === undefined) {
        return;
      }

      setIsTransitionSaving(state);

      await transitionCourseAssignmentState({
        id: parseInt(assignmentId),
        state,
      }).unwrap();
    } catch (error: any) {
      setAlert(parseErrorMessage(error), 'error');
    }

    setIsTransitionSaving(CourseAssignmentStateEnum.Invalid);
  };

  const handleNavigateTo = (
    _: string | number,
    item: CourseSessionSimpleResponse
  ) =>
    navigate(
      PathUtility.TrainingCourseSessionView(
        definitionId as string,
        assignmentId as string,
        String(item.id),
        'overview'
      )
    );

  const handleSearchTerm = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const {value} = e.target;
    setSearchTerm(value);
  };

  const handleDeleteAssignment = async () => {
    confirmDeleteHandlers.close();

    try {
      if (assignmentId === undefined) {
        return;
      }

      await deleteCourseAssignment(parseInt(assignmentId)).unwrap();

      setAlert('Course assignment deleted successfully', 'success');
      goBack();
    } catch (error: any) {
      setAlert(parseErrorMessage(error), 'error');
    }
  };

  const {breadcrumbs, fallbackTo, fallbackLabel} = useTrainingBreadcrumbs({
    mode: 'assignment',
    definitionId,
    assigneeName: data.assignedTo?.name,
  });
  const {goBack} = useBackNavigation(fallbackTo);

  const pills = CourseAssignmentPillConfig(
    data.state,
    data.active,
    undefined,
    data.dueAt
  );
  const canEdit = trainingPerms.update;
  const canDelete = trainingPerms.delete && !data.active;

  const {RenderedTabsComponent} = useUrlQueryTabV2<CourseAssignmentViewType>({
    view: (view ?? '') as CourseAssignmentViewType,
    defaultView: 'content',
    tabs: [
      {
        view: 'content',
        label: 'Content',
        component: (
          <>
            <FormViewComponentV2 formData={formData} formMeta={formMeta} />
            <PermissionGuard
              module={ModuleEnum.Training}
              operation={PermissionOperationEnum.Update}>
              <SecondaryActionPanel title="Administrator Controls">
                <Button
                  color="red"
                  onClick={() =>
                    handeTransitionAssignment(CourseAssignmentStateEnum.Open)
                  }
                  loading={isTransitionSaving == CourseAssignmentStateEnum.Open}
                  disabled={
                    isTransitionSaving != CourseAssignmentStateEnum.Invalid ||
                    data.state == CourseAssignmentStateEnum.Open
                  }
                  size="sm"
                  leftSection={<IconAlertTriangle />}
                  mr="md">
                  Re-Open
                </Button>

                <Button
                  color="red"
                  onClick={() =>
                    handeTransitionAssignment(
                      CourseAssignmentStateEnum.Complete
                    )
                  }
                  loading={
                    isTransitionSaving == CourseAssignmentStateEnum.Complete
                  }
                  disabled={
                    isTransitionSaving != CourseAssignmentStateEnum.Invalid ||
                    data.state == CourseAssignmentStateEnum.Complete
                  }
                  size="sm"
                  leftSection={<IconAlertTriangle />}>
                  Complete
                </Button>
              </SecondaryActionPanel>
            </PermissionGuard>
            <Divider my="lg" />
            <DataHeader
              titleText="Sessions"
              actions={[]}
              searchTerm={searchTerm}
              onSearchTermChange={handleSearchTerm}
              isFilterActive={false}
            />
            <DataList
              rowIcon={TrainingIcon}
              isLoading={isLoading}
              data={indexListData}
              currentPage={currentPage}
              totalRecords={totalRecords}
              recordsPerPage={maxPageRows}
              onRowClick={handleNavigateTo}
              onPageChange={handlePageChange}
              onGetPillMapping={(item: CourseSessionSimpleResponse) =>
                CourseSessionPillConfig(item?.sessionState, item.sessionType)
              }
              onRowTextRender={(item: CourseSessionSimpleResponse) =>
                item?.internalIdentifier ?? 'Unknown Session'
              }
              hasSearchTerm={searchTerm !== ''}
              hasCreationLogic={false}
              meta={undefined}
            />
          </>
        ),
      },
      {
        view: 'classroom',
        label: 'Classrooms',
        component: (
          <>
            <ViewUserCourseClassrooms
              definitionId={data.courseDefinitionId!}
              assignmentId={data.id!}
            />
          </>
        ),
      },
      {
        view: 'notifications',
        label: 'Notifications',
        disabled: !trainingPerms.notifications,
        component: (
          <>
            <NotificationsModule
              entityId={data.id}
              notificationType={NotificationModuleEnum.CourseAssignment}
              includeDescendents={true}
            />
          </>
        ),
      },
      {
        view: 'attachments',
        label: 'Attachments',
        component: (
          <>
            {data?.id && (
              <AttachmentsModule
                attachmentType={AttachmentModuleEnum.CourseAssignment}
                entityId={data.id}
              />
            )}
          </>
        ),
      },
      {
        view: 'audit',
        label: 'Audit',
        disabled: !auditPerms.view,
        component: data?.id && (
          <AuditModule
            entityId={String(data.id)}
            entityType={EntityIdentifierEnum.CourseAssignment}
          />
        ),
      },
    ],
    onSelectView: view =>
      navigate(
        PathUtility.TrainingCourseAssignmentView(
          definitionId as string,
          assignmentId as string,
          view as any
        ),
        {replace: true}
      ),
  });

  return (
    <ContentWrapper featureFlag={FeatureFlagEnum.Training}>
      <Box>
        <BackBar
          fallbackTo={fallbackTo}
          fallbackLabel={fallbackLabel}
          breadcrumbs={breadcrumbs}
        />
        {isLoading ? (
          <Spinner loadingText="Loading...." />
        ) : assignmentError || !assignment ? (
          <Alert color="red" title="Error">
            Failed to load course assignment. Please try again later.
          </Alert>
        ) : (
          <>
            <PageHeader>
              <PageHeader.Title icon={TrainingIcon}>
                {data.assignedTo?.name ?? 'Unknown name'}
              </PageHeader.Title>
              {pills.length > 0 && (
                <PageHeader.Badges>
                  {pills.map((pill, index) => (
                    <PageHeader.Badge
                      key={index}
                      color={pill.color}
                      variant="light">
                      {pill.text}
                    </PageHeader.Badge>
                  ))}
                </PageHeader.Badges>
              )}
              <PageHeader.Actions>
                {personnelPerms.view && data?.assignedToId && (
                  <PageHeader.Action
                    component={Link}
                    to={PathUtility.PersonnelView(
                      data.assignedToId,
                      'training'
                    )}>
                    View Personnel
                  </PageHeader.Action>
                )}
                {canEdit && (
                  <PageHeader.Action
                    variant="edit"
                    component={Link}
                    to={PathUtility.TrainingCourseAssignmentEdit(
                      String(definitionId),
                      String(assignmentId)
                    )}>
                    Edit
                  </PageHeader.Action>
                )}
                {canDelete && (
                  <PageHeader.Action
                    variant="delete"
                    onClick={() => confirmDeleteHandlers.open()}>
                    Delete
                  </PageHeader.Action>
                )}
              </PageHeader.Actions>
            </PageHeader>

            {RenderedTabsComponent}
          </>
        )}
      </Box>

      <ConfirmDialog
        opened={confirmDeleteOpened}
        onClose={() => confirmDeleteHandlers.close()}
        onConfirm={handleDeleteAssignment}
        confirmationButtonColor="red"
        title="Confirm Delete Course Assignment"
        body="Are you sure you want to delete this course assignment? All history and progress will be lost. This action cannot be undone."
      />
    </ContentWrapper>
  );
};

// Register the route with the new system
routeRegistry.registerPageRoute({
  backNavigation: 'page',
  path: PathUtility.TrainingCourseAssignmentView(
    ':definitionId',
    ':assignmentId',
    ':view'
  ),
  element: <ViewCourseDefinitionAssignmentPage />,
  module: ModuleEnum.Training,
  operation: PermissionOperationEnum.View,
  isPrivate: true,
});
